import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Slide,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

interface Props {
  children: React.ReactElement;
}

function HideOnScroll(props: Props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activePath, setActivePath] = useState('');
  const location = useLocation();
  
  const navItems = [
    { name: 'Collections', path: '/collections' },
    { name: 'Story', path: '/story' },
    { name: 'Journey', path: '/journey' },
    { name: 'Craftsmanship', path: '/craftsmanship' },
    { name: 'Journal', path: '/journal' },
    { name: 'Affiliate', path: '/affiliate' },
  ];

  // Parse hash URL on mount and location change
  useEffect(() => {
    // For HashRouter, the path is in the hash
    const hash = window.location.hash;
    if (hash) {
      // Remove # and any leading slash
      const path = hash.replace('#', '').replace(/^\/+/, '');
      setActivePath(`/${path}`);
    } else {
      setActivePath('/');
    }
  }, [location]);

  // Simple active check based on current hash path
  const isActive = (path: string) => {
    // Handle home page
    if (path === '/') {
      return activePath === '/' || activePath === '' || activePath === '/#';
    }
    // For other pages
    return activePath === path || activePath.startsWith(`${path}/`);
  };

  // Get current page name for debug
  console.log('Active Path:', activePath, 'Hash:', window.location.hash);

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src="/images/poreialogo.png"
                alt="Poreia Logo"
                sx={{
                  height: 40,
                  width: 'auto',
                  borderRadius: 2
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <Typography
                component={Link}
                to="/"
                sx={{
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.8rem',
                  letterSpacing: '0.2em',
                  fontWeight: 500,
                  // Remove all underline effects from logo
                  '&:after': {
                    display: 'none',
                  },
                  '&:hover': {
                    color: 'primary.main', // Keep same color
                  },
                }}
              >
                POREIA
              </Typography>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4 }}>
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <motion.div
                    key={item.name}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                  >
                    <Typography
                      component={Link}
                      to={`${item.path}`}
                      sx={{
                        textDecoration: 'none',
                        color: active ? 'secondary.main' : 'primary.main',
                        fontSize: '0.9rem',
                        letterSpacing: '0.1em',
                        fontWeight: active ? 600 : 400,
                        position: 'relative',
                        padding: '4px 0',
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          width: active ? '100%' : '0%',
                          height: '2px',
                          bottom: '-4px',
                          left: '0',
                          backgroundColor: 'secondary.main',
                          transition: 'width 0.3s ease',
                        },
                        '&:hover:after': {
                          width: '100%',
                        },
                        '&:hover': {
                          color: 'secondary.main',
                        },
                      }}
                    >
                      {item.name}
                    </Typography>
                  </motion.div>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                edge="end"
                color="primary"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 300,
            backgroundColor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              fontFamily: "'Playfair Display', serif",
              letterSpacing: '0.2em',
              textAlign: 'center',
            }}
          >
            POREIA
          </Typography>
          <List>
            <ListItem
              component={Link}
              to="/#"
              onClick={() => setDrawerOpen(false)}
              sx={{
                textDecoration: 'none',
                color: isActive('/') ? 'secondary.main' : 'primary.main',
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                backgroundColor: isActive('/') ? 'rgba(193, 154, 107, 0.1)' : 'transparent',
              }}
            >
              <ListItemText
                primary="Home"
                primaryTypographyProps={{
                  fontSize: '1.1rem',
                  letterSpacing: '0.1em',
                  fontWeight: isActive('/') ? 600 : 400,
                }}
              />
            </ListItem>
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItem
                  key={item.name}
                  component={Link}
                  to={`#${item.path}`}
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    textDecoration: 'none',
                    color: active ? 'secondary.main' : 'primary.main',
                    py: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: active ? 'rgba(193, 154, 107, 0.1)' : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: '1.1rem',
                      letterSpacing: '0.1em',
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;